/* eslint-disable */
import React from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import Swal from 'sweetalert2';
import SlidingDrawer from '../../components/SlidingDrawer';

const ICONS = {
    search: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
    add: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    committee: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    user: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
    crown: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F57C00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14" /></svg>,
    edit: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
    list: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
};

const CommitteeManagement = () => {
    const [committees, setCommittees] = React.useState([]);
    const [eligibleLeaders, setEligibleLeaders] = React.useState([]); // MLAs, MPs, MLCs
    const [loading, setLoading] = React.useState(true);
    const [filter, setFilter] = React.useState('');
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const [selectedCommitteeId, setSelectedCommitteeId] = React.useState(null);

    const [formData, setFormData] = React.useState({
        name: '',
        committeeId: '',
        description: '',
        members: [], // Array of User IDs
        leader: ''   // User ID
    });

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: `${API_BASE_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [committeesRes, leadersRes] = await Promise.all([
                api.get('/admin/committees'),
                api.get('/admin/leaders')
            ]);
            setCommittees(committeesRes.data);
            setEligibleLeaders(leadersRes.data);
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to fetch committee data', 'error');
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    const handleMemberAdd = (userId) => {
        if (!userId) return;
        if (formData.members.includes(userId)) return;

        if (formData.members.length >= 10) {
            Swal.fire('Limit Reached', 'A committee can have a maximum of 10 members.', 'warning');
            return;
        }

        setFormData(prev => ({
            ...prev,
            members: [...prev.members, userId]
        }));
    };

    const handleMemberRemove = (userId) => {
        setFormData(prev => {
            const newMembers = prev.members.filter(id => id !== userId);
            // If removed member was leader, clear leader
            const newLeader = prev.leader === userId ? '' : prev.leader;
            return { ...prev, members: newMembers, leader: newLeader };
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.committeeId || formData.members.length === 0 || !formData.leader) {
            Swal.fire('Incomplete Definition', 'Please fill all fields, add members, and select a leader.', 'warning');
            return;
        }

        try {
            Swal.fire({
                title: isEditing ? 'Updating Protocol...' : 'Establishing Protocol...',
                text: 'Formalizing committee structure.',
                didOpen: () => Swal.showLoading(),
                allowOutsideClick: false
            });

            if (isEditing) {
                await api.put(`/admin/committees/${selectedCommitteeId}`, formData);
            } else {
                await api.post('/admin/committees', formData);
            }

            Swal.fire({
                title: 'Success',
                text: `Committee successfully ${isEditing ? 'updated' : 'constituted'}.`,
                icon: 'success',
                confirmButtonColor: '#C0722C'
            });

            setIsDrawerOpen(false);
            setFormData({ name: '', committeeId: '', description: '', members: [], leader: '' });
            setIsEditing(false);
            fetchData();
        } catch (err) {
            Swal.fire('Operation Failed', err.response?.data?.error || 'System Error', 'error');
        }
    };

    const handleEdit = (committee) => {
        setFormData({
            name: committee.name,
            committeeId: committee.committeeId,
            description: committee.description || '',
            members: committee.members ? committee.members.map(m => m._id) : [],
            leader: committee.leader ? committee.leader._id : ''
        });
        setSelectedCommitteeId(committee._id);
        setIsEditing(true);
        setIsDrawerOpen(true);
    };

    const filteredCommittees = committees.filter(c =>
        c.name.toLowerCase().includes(filter.toLowerCase()) ||
        c.committeeId.toLowerCase().includes(filter.toLowerCase())
    );

    // Helpers to get user details from ID
    const getUserDetails = (id) => eligibleLeaders.find(l => l._id === id);

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto', fontFamily: '"Outfit", sans-serif' }}>
            <style>
                {`
                .transparent-scrollbar::-webkit-scrollbar {
                    width: 0px;
                    background: transparent;
                }
                .transparent-scrollbar {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                `}
            </style>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h1 style={{ margin: '0 0 8px 0', fontSize: '32px', fontWeight: '800', color: '#2C1A10' }}>Committee Governance</h1>
                    <p style={{ margin: 0, color: '#999', fontSize: '16px' }}>Constitute and manage legislative committees and oversight bodies.</p>
                </div>
                <button
                    onClick={() => {
                        setFormData({ name: '', committeeId: '', description: '', members: [], leader: '' });
                        setIsEditing(false);
                        setIsDrawerOpen(true);
                    }}
                    style={{
                        padding: '16px 32px',
                        backgroundColor: '#2C1A10',
                        color: 'white',
                        border: 'none',
                        borderRadius: '20px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        boxShadow: '0 10px 25px rgba(44, 26, 16, 0.2)',
                        transition: 'all 0.3s ease'
                    }}
                >
                    {ICONS.add}
                    Constitute Committee
                </button>
            </div>

            <div style={{ position: 'relative', marginBottom: '30px' }}>
                <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#C0722C', display: 'flex', zIndex: 2 }} >
                    {ICONS.search}
                </div>
                <input
                    type="text"
                    placeholder="Search committees by Name or ID..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{
                        width: '95%',
                        padding: '16px 20px 16px 48px',
                        borderRadius: '12px',
                        border: '1.5px solid #E5E5E5',
                        fontSize: '15px',
                        outline: 'none',
                        backgroundColor: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.02)'
                    }}
                />
            </div>

            <SlidingDrawer
                isOpen={isDrawerOpen}
                onClose={() => { setIsDrawerOpen(false); }}
                title={isEditing ? "Modify Committee" : "Constitute New Committee"}
                contentClassName="transparent-scrollbar"
            >
                <form onSubmit={handleFormSubmit} style={{ animation: 'fadeIn 0.4s ease' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Committee Name</label>
                        <input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g. Public Accounts Committee"
                            style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid #E0E0E0', fontSize: '14px', backgroundColor: '#FAFAFA' }}
                            required
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Committee Unique ID</label>
                        <input
                            value={formData.committeeId}
                            onChange={(e) => setFormData({ ...formData, committeeId: e.target.value })}
                            placeholder="e.g. PAC-2026"
                            style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid #E0E0E0', fontSize: '14px', backgroundColor: '#FAFAFA' }}
                            required
                            disabled={isEditing}
                        />
                    </div>

                    <div style={{ marginBottom: '25px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Members (Max 10)</label>

                        {/* Member Selector */}
                        <select
                            onChange={(e) => handleMemberAdd(e.target.value)}
                            value=""
                            style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid #E0E0E0', fontSize: '14px', backgroundColor: '#fff', cursor: 'pointer', marginBottom: '15px' }}
                        >
                            <option value="">+ Add Member to Committee</option>
                            {eligibleLeaders
                                .filter(leader => !formData.members.includes(leader._id))
                                .map(leader => (
                                    <option key={leader._id} value={leader._id}>
                                        {leader.firstName} {leader.lastName} ({leader.designation} - {leader.constituency})
                                    </option>
                                ))
                            }
                        </select>

                        {/* Selected Members Chips */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {formData.members.map(memberId => {
                                const user = getUserDetails(memberId);
                                if (!user) return null;
                                return (
                                    <div key={memberId} style={{
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                        padding: '8px 12px', backgroundColor: '#FFF3E0', color: '#E65100',
                                        borderRadius: '20px', fontSize: '12px', fontWeight: '600', border: '1px solid #FFE0B2'
                                    }}>
                                        <span>{user.firstName} {user.lastName}</span>
                                        <div
                                            onClick={() => handleMemberRemove(memberId)}
                                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', opacity: 0.7 }}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {formData.members.length === 0 && <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>No members selected.</div>}
                    </div>

                    <div style={{ marginBottom: '25px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Committee Leader (Chairperson)</label>
                        <select
                            value={formData.leader}
                            onChange={(e) => setFormData({ ...formData, leader: e.target.value })}
                            style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1px solid #E0E0E0', fontSize: '14px', backgroundColor: '#fff', cursor: 'pointer' }}
                            required
                            disabled={formData.members.length === 0}
                        >
                            <option value="">Select Chairperson from Members</option>
                            {formData.members.map(memberId => {
                                const user = getUserDetails(memberId);
                                if (!user) return null;
                                return (
                                    <option key={memberId} value={memberId}>
                                        {user.firstName} {user.lastName}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div style={{ marginBottom: '30px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Mandate / Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Committee purpose and jurisdiction..."
                            style={{ width: '100%', height: '100px', padding: '14px', borderRadius: '12px', border: '1px solid #E0E0E0', fontSize: '14px', backgroundColor: '#FAFAFA', resize: 'none', fontFamily: 'inherit' }}
                        />
                    </div>

                    <button
                        type="submit"
                        style={{
                            width: '100%',
                            padding: '18px',
                            backgroundColor: '#C0722C',
                            color: 'white',
                            border: 'none',
                            borderRadius: '16px',
                            fontWeight: '800',
                            fontSize: '16px',
                            cursor: 'pointer',
                            boxShadow: '0 10px 20px rgba(192, 114, 44, 0.2)',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isEditing ? 'Update Committee Protocols' : 'Establish Committee'}
                    </button>
                </form>
            </SlidingDrawer>

            {/* Committee Table */}
            <div style={{ backgroundColor: 'white', borderRadius: '28px', boxShadow: '0 15px 45px rgba(0,0,0,0.03)', overflow: 'hidden', border: '1px solid #F0F0F0' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', textAlign: 'left' }}>
                    <thead style={{ backgroundColor: '#FDF8F3' }}>
                        <tr style={{ color: '#888', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                            <th style={{ padding: '25px 30px' }}>Committee Details</th>
                            <th style={{ padding: '25px 30px' }}>Chairperson</th>
                            <th style={{ padding: '25px 30px' }}>Composition</th>
                            <th style={{ padding: '25px 30px', textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="4" style={{ padding: '60px', textAlign: 'center', color: '#888' }}>LOADING RECORDS...</td></tr>
                        ) : filteredCommittees.length === 0 ? (
                            <tr>
                                <td colSpan="4" style={{ padding: '80px', textAlign: 'center' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ width: '80px', height: '80px', backgroundColor: '#FDF8F3', borderRadius: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C0722C', marginBottom: '20px' }}>
                                            {ICONS.committee}
                                        </div>
                                        <h3 style={{ color: '#2C1A10', margin: 0 }}>No Committees Found</h3>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredCommittees.map(committee => (
                                <tr key={committee._id} style={{ borderBottom: '1px solid #F5F5F5' }}>
                                    <td style={{ padding: '25px 30px' }}>
                                        <div style={{ fontSize: '16px', fontWeight: '800', color: '#2C1A10' }}>{committee.name}</div>
                                        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ color: '#C0722C', fontWeight: '700' }}>ID:</span> {committee.committeeId}
                                        </div>
                                    </td>
                                    <td style={{ padding: '25px 30px' }}>
                                        {committee.leader ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ padding: '6px', backgroundColor: '#FFF8E1', borderRadius: '50%', color: '#F57C00' }}>{ICONS.crown}</div>
                                                <div>
                                                    <div style={{ fontWeight: '700', color: '#2C1A10', fontSize: '14px' }}>{committee.leader.firstName} {committee.leader.lastName}</div>
                                                    <div style={{ fontSize: '11px', color: '#888' }}>{committee.leader.designation}</div>
                                                </div>
                                            </div>
                                        ) : <span style={{ color: '#999', fontStyle: 'italic' }}>Pending Appointment</span>}
                                    </td>
                                    <td style={{ padding: '25px 30px' }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', backgroundColor: '#F5F5F5', borderRadius: '12px', fontSize: '13px', fontWeight: '600', color: '#555' }}>
                                            {ICONS.user}
                                            {committee.members ? committee.members.length : 0} / 10 Members
                                        </div>
                                    </td>
                                    <td style={{ padding: '25px 30px', textAlign: 'right' }}>
                                        <button
                                            onClick={() => handleEdit(committee)}
                                            style={{
                                                padding: '12px 20px',
                                                backgroundColor: '#fff',
                                                border: '1px solid #E0E0E0',
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '13px',
                                                fontWeight: '700',
                                                color: '#2C1A10',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseOver={(e) => { e.currentTarget.style.borderColor = '#C0722C'; e.currentTarget.style.color = '#C0722C'; }}
                                            onMouseOut={(e) => { e.currentTarget.style.borderColor = '#E0E0E0'; e.currentTarget.style.color = '#2C1A10'; }}
                                        >
                                            {ICONS.edit} Manage
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CommitteeManagement;
